import * as d3 from 'd3';
import {format} from 'd3-format';
import {scaleLinear, scaleLog} from 'd3-scale';

import {xAxisLabels} from '../../components/WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {
  XAxisType,
  YAxisType,
} from '../../components/WorkspaceDrawer/Settings/types';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {truncateDisplayName} from '../runhelpers';
import {textWidth} from '../text';
import {PlotFontSize} from './plotFontSize';
import {Line, Timestep} from './types';

export const axisLabelMargin = 13; // Constant distance between label and axis line

const SCALE_FUNCTIONS: {
  linear: () => d3.ScaleLinear<number, number>;
  log: () => d3.ScaleLogarithmic<number, number>;
} = {
  linear: scaleLinear,
  log: scaleLog,
};

/**
 * Determine a fallback base for a logarithmic scale when the domain[0] = 0. This value breaks d3 plots so we have to find a new base that's smaller than the larger end of the domain but proportional so that we get a logarithmic looking chart. I don't know if this algorithm is perfect, but it works okay
 */
export function determineLogBase(domain: [number, number]) {
  let base = 1;
  while (base >= domain[1] / 10) {
    base = base / 100;
  }
  return base;
}
/**
 * Copied from react-vis because it was a private function
 * React vis uses this internally to create the scale for
 * axis ticks
 *
 * Create a scale function from the scale object.
 *
 * @returns {*} Scale function.
 */
export function getScaleFnFromScaleObject(scaleObject: {
  domain: number[];
  type: XAxisType | YAxisType;
}) {
  if (!scaleObject) {
    return null;
  }
  const {type, domain} = scaleObject;
  const modDomain =
    domain[0] === domain[1]
      ? domain[0] === 0
        ? [-1, 0]
        : [-domain[0], domain[0]]
      : domain;
  /**
   * Logarithmic scales cannot display when the first number is 0.
   */
  if (scaleObject.type === 'log' && modDomain[0] === 0) {
    modDomain[0] = determineLogBase(modDomain as [number, number]);
  }
  const scale = (SCALE_FUNCTIONS as any)[type]().domain(modDomain);
  return scale;
}

export function formatXAxisNonTime(
  xType: string,
  xMin: number,
  xMax: number,
  timestep?: Timestep
) {
  if (xType === 'time') {
    return undefined;
  }

  // Decimal notation with 4 significant digits
  const formatWithSI = format('.4~s');
  const formatWithoutSI = format('.4~r');

  if (timestep) {
    // If it's in relative time (e.g. hours, minutes, seconds), then we shouldn't use SI units
    // because it's confusing to see "10m" to mean "0.01 hours", since to the user that would look like 10 minutes.
    return formatWithoutSI;
  }

  // Avoid using milli SI units, since it's weird to see "800m" instead of "0.8"
  const delta = xMax - xMin;
  const absMin = Math.abs(xMin);
  const absMax = Math.abs(xMax);
  const minInMilliRange = absMin >= 0.001 && absMin < 1;
  const maxInMilliRange = absMax >= 0.001 && absMax < 1;
  if (minInMilliRange && maxInMilliRange) {
    return formatWithoutSI;
  }
  if ((minInMilliRange || maxInMilliRange) && delta < 1000) {
    return formatWithoutSI;
  }

  return formatWithSI;
}

// Format the y-axis for the line chart, which doesn't allow
// time on the y axis
// https://weightsandbiases.slack.com/archives/C07JHLX0HJ5/p1744416332311149
export const formatYAxis = (tick: number): string => format('.8~g')(tick);

export const getYAxisWidth = (
  labels: string[],
  plotFontSize: PlotFontSize = 'small'
) => {
  if (labels.length === 0) {
    return 0;
  }
  // Find the length of the longest label
  const maxLabelWidth = Math.max(
    ...labels.map(l => textWidth(truncateDisplayName(l), plotFontSize))
  );

  // Add the distance between the label and axis line
  return maxLabelWidth + axisLabelMargin;
};

export function prettyXAxisLabel(xAxis: string, lines: Line[]) {
  /*
   * Make a pretty xAxisLabel
   * Really all we do is:
   * 1) Change special keys _runtime and _timestamp and _step to
   * 2) take times and return (sec), (min) or (hour)
   * otherwise just return the label
   */
  if (!(xAxis in xAxisLabels)) {
    // LB: This is the common case
    return xAxis;
  }

  if (!lines || lines.length === 0) {
    // we can't figure out if its seconds or mins or whatever...
    return 'Time';
  }

  // all timesteps should be the same so we can just look at the first one
  const timestep = lines.length > 0 ? lines[0].timestep || '' : '';
  let label = xAxisLabels[xAxis] || '';
  if (xAxis === '_runtime' || xAxis === '_absolute_runtime') {
    label = 'Time' + (timestep ? ' (' + timestep + ')' : '');
  }
  return label;
}
